import { forwardRef, cloneElement, isValidElement, type ReactElement } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-md font-medium tracking-tight transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)] disabled:pointer-events-none disabled:opacity-50 whitespace-nowrap",
  {
    variants: {
      variant: {
        primary:
          "bg-[var(--brand)] text-[var(--bg)] border border-[var(--brand)] font-semibold hover:bg-[var(--brand-strong)] hover:-translate-y-px",
        ghost:
          "bg-transparent text-white border border-white/15 hover:border-white/25 hover:bg-white/[0.03]",
        secondary:
          "bg-transparent text-white border border-white/15 hover:border-white/25 hover:bg-white/[0.03]",
        dark:
          "bg-[#1a1d23] text-white border border-white/10 hover:bg-[#23272f]",
        outline:
          "bg-transparent text-white border border-[var(--border-strong)] hover:bg-white/[0.04]",
        link:
          "bg-transparent text-[var(--brand)] border border-transparent px-0 hover:underline",
      },
      size: {
        sm: "h-8 px-3.5 text-[13px]",
        md: "h-10 px-5 text-sm",
        lg: "h-12 px-6 text-[15px]",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Render the button's styling onto its single child element instead of a
   * native <button>. Use for CTAs that navigate: `<Button asChild><Link
   * href>…</Link></Button>` renders ONE styled <a>, avoiding the invalid
   * <a><button> nesting (audit DX-05).
   *
   * Contract: the Button's remaining props and ref are FORWARDED onto the child,
   * and `className`/`style` are merged (variant styling + child styling). The
   * child's own props win on conflict, so a handler set on the child is never
   * overwritten. This is a deliberately small slot, not a prop-dropping cast.
   */
  asChild?: boolean;
}

type SlotChildProps = {
  className?: string;
  style?: React.CSSProperties;
} & Record<string, unknown>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const classes = cn(buttonVariants({ variant, size }), className);
    if (asChild && isValidElement(children)) {
      // Forward the Button's props + ref onto the child rather than dropping
      // them; merge className/style; child props take precedence on conflict.
      const child = children as ReactElement<SlotChildProps>;
      return cloneElement(child, {
        ...props,
        ...child.props,
        ref,
        className: cn(classes, child.props.className),
        style: { ...(props.style as React.CSSProperties), ...child.props.style },
      } as SlotChildProps);
    }
    return (
      <button ref={ref} className={classes} {...props}>
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
